"use client"

import Link from "next/link"
import { ArrowRight, ArrowUpRight } from "lucide-react"

import { Seo } from "@/components/generals/seo"
import { Section, SectionHeader } from "@/components/generals/section"
import { Reveal } from "@/components/generals/reveal"
import { Button } from "@/components/ui/button"
import CommunityLogoGrid from "@/components/community-logo-grid"
import Hero from "@/components/hero"
import SocialProof from "@/components/social-proof"
import InsightsSection from "@/components/insights-section"
import { singleLogoPrice } from "@/constants/site"

const STEPS = [
  {
    title: "Write one sentence",
    body: "What the business does, who it is for, how it should feel. That is the whole brief.",
  },
  {
    title: "Choose, or don't",
    body: "Pick the mark type, the style and the palette. Leave any of them to the AI and it decides from your brief.",
  },
  {
    title: "Take the files",
    body: "Unlock the logo once and download the full-resolution PNG plus the SVG, PDF and EPS a printer will ask for.",
  },
]

const FORMATS = [
  { name: "PNG", detail: "Full resolution, for the web, social and slides." },
  { name: "SVG", detail: "True vector, from favicon to shopfront." },
  { name: "PDF", detail: "What a print shop or signmaker asks for." },
  { name: "EPS", detail: "The older vector standard suppliers still want." },
]

export default function Home() {
  return (
    <>
      <Seo
        fullTitle="AI Logo Maker: describe your brand, leave with the files"
        description="Describe your business in one sentence and get a finished logo, then the print-ready PNG, SVG, PDF and EPS files to go with it."
      />

      <Hero />
      <SocialProof />

      {/* The gallery. The one grid on the page, and the only colour. */}
      <Section id="community" size="md">
        <div className="container">
          <div className="mx-auto max-w-6xl">
            <SectionHeader
              title="Made here this week"
              description="Every public logo made with the tool, newest first."
              action={
                <Link
                  href="/community"
                  className="inline-flex items-center gap-1.5 rounded-sm text-small text-ink-muted transition-colors duration-fast ease-out hover:text-ink"
                >
                  Browse all <ArrowUpRight className="size-3.5" aria-hidden />
                </Link>
              }
            />

            <div className="mt-12">
              <CommunityLogoGrid hideShowMoreButton />
            </div>
          </div>
        </div>
      </Section>

      {/* The whole offer in one band: how it works, then what it leaves you with. */}
      <Section id="how-it-works" size="md" tone="sunken" divider>
        <div className="container">
          <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="flex flex-col gap-6 lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
              <h2 className="font-display text-h2 text-balance">
                Three steps, then the files are yours.
              </h2>
              <p className="text-lead text-ink-muted text-pretty">
                No brief, no back and forth, no waiting on a quote. One payment of{" "}
                <span className="text-ink">${singleLogoPrice}</span> gets you three concepts and
                every file, yours to keep.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button asChild>
                  <Link href="/#hero">
                    Start with a sentence <ArrowRight aria-hidden />
                  </Link>
                </Button>
                <Button asChild variant="ghost">
                  <Link href="/pricing">What is included</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ol className="border-t border-line-strong">
                {STEPS.map((step, index) => (
                  <Reveal
                    as="li"
                    key={step.title}
                    delay={index * 70}
                    className="grid gap-2 border-b border-line py-6 sm:grid-cols-[3.5rem_minmax(0,1fr)] sm:gap-6"
                  >
                    <span className="type-eyebrow tabular-nums sm:pt-1.5">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="flex flex-col gap-1.5">
                      <h3 className="font-display text-h4 text-balance">{step.title}</h3>
                      <p className="text-small text-ink-muted text-pretty">{step.body}</p>
                    </div>
                  </Reveal>
                ))}
              </ol>

              <div className="mt-10 flex flex-col gap-5">
                <h3 className="text-small font-medium text-ink">What you take away</h3>
                <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
                  {FORMATS.map((format) => (
                    <div
                      key={format.name}
                      className="flex flex-col gap-1 border-t border-line pt-3"
                    >
                      <dt className="font-display text-h4">{format.name}</dt>
                      <dd className="text-caption text-ink-muted text-pretty">{format.detail}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <InsightsSection />

      {/* The one dark band on the page. */}
      <Section id="start" size="lg" tone="ink">
        <div className="container">
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-8 text-center">
            <h2 className="type-display text-balance">Your brand, in a minute.</h2>
            <p className="max-w-lg text-lead text-pretty opacity-70">
              ${singleLogoPrice} per logo. Three concepts, every file format, no subscription needed.
            </p>
            <Button asChild size="lg" variant="inverse">
              <Link href="/#hero">
                Start with a sentence <ArrowRight aria-hidden />
              </Link>
            </Button>
          </div>
        </div>
      </Section>
    </>
  )
}
